/**
 * The BFF's feature-flag facade: one read contract, and nothing outside it
 * knows what backs the answer.
 *
 * Today the backing is a static manifest of the effective prod values
 * (`feature-flags.json`, captured live from the prod PostHog project — #145).
 * When the PostHog plumbing lands (#116 / ADR-0002) only the source behind
 * these functions changes; no call site moves. That is why the read is
 * **synchronous** and there is no loading state to unwind later: ADR-0002 has
 * the BFF evaluate server-side and SSR-bootstrap the values, so a flag is
 * present at first render even once PostHog backs it. The old stack's
 * `admin-route.tsx` `loading` wait is a deliberate divergence, not an omission.
 *
 * Lives in `lib/` for the reason `@/lib/roles` does (client/docs/architecture.md,
 * "The dependency rule"): the consumers read it from opposite sides — L5's
 * `useFeatureFlag` and the L6 chrome gating the Admin tab (#152) on one, the root
 * route's server-side maintenance decision (#153) on the other — so the one copy
 * has to sit below both graphs.
 *
 * Python carries its own manifest of the ten keys it serves
 * (`server/app/core/feature_flags.json`). Three keys are read by both processes
 * and are pinned between the two sides — see `feature-flags-drift.unit.test.ts`
 * and its Python counterpart. Deliberately not one shared file: each process
 * carries only what it serves.
 */

import { z } from "zod";

import manifestJson from "./feature-flags.json";

/**
 * How a key is read. The capture recorded no boolean for a `payload` key
 * because nothing reads one, so the facade offers none rather than inventing
 * a value — hence three kinds and two typed accessors instead of one.
 */
type FeatureFlagKind = "boolean" | "boolean+payload" | "payload";

const readsAsBoolean = (kind: FeatureFlagKind): boolean => kind !== "payload";
const carriesPayload = (kind: FeatureFlagKind): boolean => kind !== "boolean";

/**
 * The key set, and how each key is read. **This is the load-bearing constant**:
 * it is the whole inventory this process serves, and `feature-flags.json` is
 * checked against it at import. The thirteen further flags the prod project
 * holds are read by nothing and are deliberately absent (#52, "Out of scope").
 */
const FEATURE_FLAG_KINDS = {
  enable_maintenance: "boolean+payload",
  enable_notifications: "boolean",
  enable_admin_tab: "boolean",
  enable_simulate_user: "boolean",
  enable_target_cost_upload: "boolean",
  push_to_community_specs: "boolean",
  enable_community_map: "boolean",
  content_user_permissions: "payload",
  content_create_visualization_dialog_splash_image_url: "payload",
} as const satisfies Record<string, FeatureFlagKind>;

export type BffFeatureFlagKey = keyof typeof FEATURE_FLAG_KINDS;

export const BFF_FEATURE_FLAG_KEYS = Object.keys(
  FEATURE_FLAG_KINDS,
) as readonly BffFeatureFlagKey[];

type KeysOfKind<Kinds extends FeatureFlagKind> = {
  [Key in BffFeatureFlagKey]: (typeof FEATURE_FLAG_KINDS)[Key] extends Kinds ? Key : never;
}[BffFeatureFlagKey];

/** Keys with a boolean to read. */
export type BffBooleanFeatureFlagKey = KeysOfKind<"boolean" | "boolean+payload">;
/** Keys with a payload to read. */
export type BffPayloadFeatureFlagKey = KeysOfKind<"payload" | "boolean+payload">;

/** The payload shape behind each payload-capable key. */
export interface BffFeatureFlagPayloads {
  /** Set only while maintenance is on; the takeover renders "Expected back …". */
  enable_maintenance: { back_at: string };
  /**
   * Empty by owner call — prod's two per-user overrides are deliberately not
   * carried into Atlas, to keep PII out of checked-in code. Their shape stays
   * on #145 rather than in a type here, so nothing in the tree describes data
   * the app will never hold.
   */
  content_user_permissions: readonly JsonValue[];
  content_create_visualization_dialog_splash_image_url: string;
}

/**
 * Anything a PostHog payload can be. Spelled out rather than left as `unknown`
 * because the snapshot crosses the SSR boundary, and the router will only
 * dehydrate a type it can prove is serializable.
 */
export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/**
 * The shape behind each payload key, checked where a payload enters — the
 * manifest at import, an override at parse. Without this `BffFeatureFlagPayloads`
 * is a cast: a mis-shaped entry would reach a call site typed as something it is
 * not and fail there instead of here. It is also the seam ADR-0002 needs, where
 * these values stop being hand-reviewed JSON and start arriving from PostHog.
 *
 * Non-strict on purpose — an extra field from a future PostHog payload is not a
 * reason to refuse the flag.
 */
const PAYLOAD_SCHEMAS: Record<BffPayloadFeatureFlagKey, z.ZodType> = {
  enable_maintenance: z.object({ back_at: z.string() }),
  content_user_permissions: z.array(z.unknown()),
  content_create_visualization_dialog_splash_image_url: z.string(),
};

function isPayloadKey(key: BffFeatureFlagKey): key is BffPayloadFeatureFlagKey {
  return Object.hasOwn(PAYLOAD_SCHEMAS, key);
}

/** Check a payload against the shape the accessor promises for that key. */
function validatePayload(source: string, key: BffFeatureFlagKey, payload: unknown): void {
  if (!isPayloadKey(key)) return;
  const result = PAYLOAD_SCHEMAS[key].safeParse(payload);
  if (!result.success) {
    throw new Error(`${source}: "${key}" payload is mis-shaped — ${z.prettifyError(result.error)}`);
  }
}

/**
 * Freeze a payload through, not just at the top. The snapshot is module-global
 * and lives for the life of the process, so a consumer that sorted, spliced, or
 * reassigned into a payload it was handed would corrupt every later read — in
 * every SSR pass the process serves. Frozen rather than copied per read because
 * the read is on the render path and payloads are read far more often than the
 * snapshot moves.
 */
function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== "object" || Object.isFrozen(value)) return value;
  for (const nested of Object.values(value)) deepFreeze(nested);
  return Object.freeze(value);
}

/** One key's resolved state. `payload` is absent when the key carries none. */
export interface FeatureFlagState {
  value?: boolean;
  payload?: JsonValue;
}

/** Every key's resolved state — what the SSR bootstrap ships to the browser. */
export type FeatureFlagSnapshot = Readonly<Record<BffFeatureFlagKey, FeatureFlagState>>;

interface ManifestEntry extends FeatureFlagState {
  note?: string;
}

/**
 * Reduce the manifest file to the snapshot the facade reads, failing loudly on
 * anything the kind table does not account for. This is what lets the rest of
 * the module treat the JSON as typed: an added, removed, or mis-shaped entry
 * cannot resolve to a silent default.
 */
function validateState(source: string, key: BffFeatureFlagKey, raw: unknown): FeatureFlagState {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(`${source}: "${key}" is not a {value, payload} object — got ${String(raw)}.`);
  }
  const entry = raw as { value?: unknown; payload?: unknown };
  const kind = FEATURE_FLAG_KINDS[key];
  const hasValue = entry.value !== undefined;
  const hasPayload = entry.payload !== undefined;
  if (hasValue && typeof entry.value !== "boolean") {
    throw new Error(`${source}: "${key}" has a non-boolean value.`);
  }
  if (readsAsBoolean(kind) && !hasValue) {
    throw new Error(`${source}: "${key}" is read as a boolean but declares no value.`);
  }
  if (!readsAsBoolean(kind) && hasValue) {
    throw new Error(
      `${source}: "${key}" is read only for its payload, so a value is not a captured ` +
        "fact. Change its kind if something now reads it as a boolean.",
    );
  }
  if (!carriesPayload(kind) && hasPayload) {
    throw new Error(
      `${source}: "${key}" carries a payload but is not payload-capable. ` +
        'Change its kind to "boolean+payload".',
    );
  }
  if (kind === "payload" && !hasPayload) {
    throw new Error(`${source}: "${key}" is read for its payload but declares none.`);
  }
  if (hasPayload) validatePayload(source, key, entry.payload);
  return hasPayload
    ? { ...(hasValue && { value: entry.value as boolean }), payload: entry.payload as JsonValue }
    : { value: entry.value as boolean };
}

function readManifest(flags: Record<string, ManifestEntry>): FeatureFlagSnapshot {
  const declared = new Set<string>(BFF_FEATURE_FLAG_KEYS);
  const unaccounted = Object.keys(flags).filter((key) => !declared.has(key));
  if (unaccounted.length > 0) {
    throw new Error(
      `feature-flags.json declares keys the facade does not serve: ${unaccounted.join(", ")}. ` +
        "Add them to FEATURE_FLAG_KINDS or remove them — a key with no kind has no reader.",
    );
  }

  const snapshot = {} as Record<BffFeatureFlagKey, FeatureFlagState>;
  for (const key of BFF_FEATURE_FLAG_KEYS) {
    const entry = flags[key];
    if (!entry) {
      throw new Error(`feature-flags.json is missing the "${key}" entry.`);
    }
    snapshot[key] = validateState("feature-flags.json", key, entry);
  }
  return deepFreeze(snapshot);
}

/** The captured effective-prod values — the floor every read falls back to. */
export const BFF_FEATURE_FLAG_MANIFEST: FeatureFlagSnapshot = readManifest(
  manifestJson.flags as Record<string, ManifestEntry>,
);

// --- The local override -----------------------------------------------------

/**
 * The one convention both processes use, so a flag flipped for local work
 * behaves the same in the BFF and in Python: a single JSON blob in
 * `FEATURE_FLAG_OVERRIDES`, keyed by flag key.
 *
 * ```
 * FEATURE_FLAG_OVERRIDES={"enable_admin_tab": false,
 *                         "enable_maintenance": {"value": true,
 *                                                "payload": {"back_at": "2026-09-01T18:00:00Z"}}}
 * ```
 *
 * A bare boolean sets the value; the object form sets a value, a payload, or
 * both. The object form is the *only* way to set a payload — never a bare
 * payload — because a payload that happened to be an object with a `value`
 * field would otherwise be unreadable. A boolean-per-flag scheme was ruled out
 * for the same reason: four of the sixteen keys are payload-valued.
 *
 * Read in `local` and `test` only. It is per-environment config, so it is read
 * once from the process environment and never per request.
 */
const OVERRIDE_ENV_VAR = "FEATURE_FLAG_OVERRIDES";

const OVERRIDABLE_ENVIRONMENTS = new Set(["local", "test"]);

export type FeatureFlagOverrides = Partial<Record<BffFeatureFlagKey, FeatureFlagState>>;

// Once per key per process. The same blob is parsed twice on the server — the
// facade's best-effort application at import, then the startup plugin's — and
// two identical lines read as two different problems.
const reportedIgnoredKeys = new Set<string>();

function isServedKey(key: string): key is BffFeatureFlagKey {
  return Object.hasOwn(FEATURE_FLAG_KINDS, key);
}

function overrideFor(key: BffFeatureFlagKey, raw: unknown): FeatureFlagState {
  const kind = FEATURE_FLAG_KINDS[key];

  if (typeof raw === "boolean") {
    if (!readsAsBoolean(kind)) {
      throw new Error(
        `${OVERRIDE_ENV_VAR}: "${key}" is read only for its payload, so a boolean overrides ` +
          `nothing. Use {"payload": …}.`,
      );
    }
    return { value: raw };
  }

  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(
      `${OVERRIDE_ENV_VAR}: "${key}" must be a boolean or an object with "value" and/or ` +
        `"payload" — got ${JSON.stringify(raw)}.`,
    );
  }

  const fields = raw as { value?: unknown; payload?: unknown };
  const unknown = Object.keys(fields).filter((field) => field !== "value" && field !== "payload");
  if (unknown.length > 0) {
    throw new Error(
      `${OVERRIDE_ENV_VAR}: "${key}" has unknown field(s) ${unknown.join(", ")}. An override is a ` +
        `boolean or an object with "value" and/or "payload"; a payload always goes under "payload".`,
    );
  }

  const state: FeatureFlagState = {};
  if ("value" in fields) {
    if (typeof fields.value !== "boolean") {
      throw new Error(`${OVERRIDE_ENV_VAR}: "${key}" has a non-boolean "value".`);
    }
    if (!readsAsBoolean(kind)) {
      throw new Error(`${OVERRIDE_ENV_VAR}: "${key}" is read only for its payload.`);
    }
    state.value = fields.value;
  }
  if ("payload" in fields) {
    if (!carriesPayload(kind)) {
      throw new Error(`${OVERRIDE_ENV_VAR}: "${key}" carries no payload.`);
    }
    validatePayload(OVERRIDE_ENV_VAR, key, fields.payload);
    // Straight from JSON.parse, so it is a JsonValue by construction.
    state.payload = fields.payload as JsonValue;
  }
  if (Object.keys(state).length === 0) {
    throw new Error(
      `${OVERRIDE_ENV_VAR}: "${key}" overrides nothing — give it a value or a payload.`,
    );
  }
  return state;
}

/**
 * Parse the override blob. Keys this process does not serve are **ignored**,
 * not rejected: one blob configures both processes, and seven of the sixteen
 * keys belong to Python alone. They are logged by name, because a foreign key and a
 * typo look identical from here and only the log tells them apart. Everything
 * else fails loudly.
 */
export function parseFeatureFlagOverrides(raw: string | null | undefined): FeatureFlagOverrides {
  if (raw === null || raw === undefined || raw.trim() === "") return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (cause) {
    throw new Error(`${OVERRIDE_ENV_VAR} is not valid JSON: ${(cause as Error).message}`, {
      cause,
    });
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`${OVERRIDE_ENV_VAR} must be a JSON object keyed by flag key.`);
  }

  const overrides: FeatureFlagOverrides = {};
  const ignored: string[] = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (!isServedKey(key)) {
      ignored.push(key);
      continue;
    }
    overrides[key] = overrideFor(key, value);
  }
  const unreported = ignored.filter((key) => !reportedIgnoredKeys.has(key));
  if (unreported.length > 0) {
    // Named rather than dropped in silence: a foreign key is expected here —
    // one blob configures both processes — but so is a typo, and the two are
    // indistinguishable from this side. Saying which keys did nothing is what
    // makes the typo findable.
    for (const key of unreported) reportedIgnoredKeys.add(key);
    console.info(
      `[feature-flags] ${OVERRIDE_ENV_VAR}: ignoring ${unreported.join(", ")} — not served by ` +
        "this process. Expected for Python's own keys; check the spelling otherwise.",
    );
  }
  return overrides;
}

/**
 * The overrides this process's environment asks for, if it may ask. Exported
 * for its own test only — the module applies it at import, and no caller should
 * re-read the environment on a request path.
 */
export function overridesFromEnvironment(): FeatureFlagOverrides {
  // Guarded rather than assumed: this module is in the client bundle too, and
  // the browser has no process. There it resolves to the manifest and is then
  // handed the server's snapshot by the root route's bootstrap.
  if (typeof process === "undefined" || typeof process.env !== "object" || process.env === null) {
    return {};
  }
  const raw = process.env[OVERRIDE_ENV_VAR];
  if (!raw) return {};

  // Re-derived rather than read from `client/core/settings.ts`, which resolves
  // the same variable: that module pulls in dotenv and `node:path`, and this one
  // is in the client bundle. The duplication is the dependency rule's price.
  const environment = (process.env.ENVIRONMENT_NAME ?? "local").toLowerCase();
  if (!OVERRIDABLE_ENVIRONMENTS.has(environment)) {
    console.warn(
      `[feature-flags] ignoring ${OVERRIDE_ENV_VAR} in the "${environment}" environment — ` +
        "flag overrides are a local/test affordance. Change the manifest and deploy instead.",
    );
    return {};
  }
  return parseFeatureFlagOverrides(raw);
}

// --- The store --------------------------------------------------------------

// One module-level snapshot, deliberately: these values are static
// per-environment config, identical for every request the process serves, so
// there is nothing per-request to leak between SSR passes (the reason
// `lib/global-state.ts` is per-router and this is not).
let current: FeatureFlagSnapshot = BFF_FEATURE_FLAG_MANIFEST;

// Subscribers exist for the source ADR-0002 has coming, not for today: once
// `posthog-js` refreshes the browser's flags in session, a flip has to reach a
// mounted consumer, and a hook that merely read during render would force every
// call site to grow a subscription then. Today only the bootstrap and the test
// helper move the snapshot.
const listeners = new Set<() => void>();

function canonical(snapshot: FeatureFlagSnapshot): string {
  return JSON.stringify(BFF_FEATURE_FLAG_KEYS.map((key) => [key, snapshot[key]]));
}

function publish(next: FeatureFlagSnapshot): void {
  if (canonical(next) === canonical(current)) return;
  // Here rather than at each caller so the bootstrap's snapshot — which arrives
  // deserialized from the SSR payload and is nobody's frozen object — is as
  // safe to hand out as the manifest's.
  current = deepFreeze(next);
  for (const listener of listeners) listener();
}

/**
 * Layer overrides over whatever is currently resolved.
 *
 * Over *current*, not over the manifest, so two calls accumulate rather than
 * the second silently discarding the first — which is what the test helper
 * needs: `setFeatureFlags({a})` then `setFeatureFlags({b})` in one test must
 * leave both set. At import the current snapshot *is* the manifest, so the
 * environment's overrides land on the captured values either way, and
 * `resetFeatureFlags()` stays the one way back to a bare manifest.
 */
export function overrideFeatureFlags(overrides: FeatureFlagOverrides): void {
  const next = {} as Record<BffFeatureFlagKey, FeatureFlagState>;
  for (const key of BFF_FEATURE_FLAG_KEYS) {
    const override = overrides[key];
    next[key] = override ? { ...current[key], ...override } : current[key];
  }
  publish(next);
}

/**
 * What the SSR pass hands the browser. Carried on the router's dehydrated
 * payload (`router.tsx`) rather than in a loader or a component, because that
 * is the one hook that runs after the payload is read and **before the first
 * client render** — a flag read in between would be the manifest's, disagreeing
 * with the HTML it is hydrating whenever a local override is set.
 *
 * It is also the shape ADR-0002 needs next: these are the values that become
 * `posthog.init(…, { bootstrap })` when PostHog backs the facade.
 */
export interface FeatureFlagBootstrap {
  featureFlags: FeatureFlagSnapshot;
}

/** Server side: the snapshot to ship. */
export function dehydrateFeatureFlags(): FeatureFlagBootstrap {
  return { featureFlags: current };
}

/**
 * Client side: adopt the snapshot the server resolved. Idempotent, and a no-op
 * when the payload carries none — a boot with no SSR pass behind it keeps the
 * manifest rather than blanking every flag.
 *
 * A snapshot that does not survive the same checks the manifest is held to is
 * refused whole rather than key by key: it can only mean the server and this
 * bundle disagree about the key set or its shapes, and adopting the good half
 * would turn every other key's read into a crash — or worse, hand a consumer a
 * value of the wrong type under a contract that says otherwise. Warn and keep
 * the manifest, which is the shippable arm. The payload arrives deserialized
 * from HTML, so "our own server sent it" is not a shape guarantee.
 */
export function hydrateFeatureFlags(dehydrated: Partial<FeatureFlagBootstrap> | undefined): void {
  const snapshot = dehydrated?.featureFlags;
  if (!snapshot) return;

  const validated = {} as Record<BffFeatureFlagKey, FeatureFlagState>;
  for (const key of BFF_FEATURE_FLAG_KEYS) {
    try {
      validated[key] = validateState("the SSR bootstrap", key, snapshot[key]);
    } catch (cause) {
      console.warn(
        `[feature-flags] ignoring the SSR bootstrap — ${(cause as Error).message} Serving the ` +
          "captured manifest instead.",
      );
      return;
    }
  }
  publish(validated);
}

/** Back to the captured manifest, overrides and bootstrap discarded. */
export function resetFeatureFlags(): void {
  publish(BFF_FEATURE_FLAG_MANIFEST);
}

/** Subscribe to snapshot changes. The shape `useSyncExternalStore` wants. */
export function subscribeToFeatureFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// --- The read contract ------------------------------------------------------

/**
 * Read a flag. Synchronous by contract — never wrap this in a loading state.
 *
 * The key type already excludes the keys with no boolean to read; the throw is
 * for the callers types cannot reach (plain JS, a cast, a key off the wire).
 * Defaulting to `false` there would invent the one thing the capture refused to
 * invent, and Python's half raises for the same reason.
 */
export function featureFlag(key: BffBooleanFeatureFlagKey): boolean {
  const { value } = current[key];
  if (value === undefined) {
    throw new Error(
      `"${key}" is read only for its payload — no boolean was captured for it because nothing ` +
        "reads one. Use featureFlagPayload().",
    );
  }
  return value;
}

/** Read a flag's payload, or `undefined` when the key carries none. */
export function featureFlagPayload<Key extends BffPayloadFeatureFlagKey>(
  key: Key,
): BffFeatureFlagPayloads[Key] | undefined {
  return current[key].payload as BffFeatureFlagPayloads[Key] | undefined;
}

// Applied at import, best-effort: a server-side reader that runs before Nitro's
// plugins (or a plain Node script importing the facade) still gets the override
// when the variable is exported at the OS level. It cannot be the only
// application point, because this module must stay out of dotenv's reach —
// `src/server/runtime/feature-flags.ts` is the one that runs after the env file
// is loaded, and re-applying the same overrides there is a no-op.
overrideFeatureFlags(overridesFromEnvironment());
